// src/pages/ListinoAdmin.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import "./listino-admin.css";

// Endpoints backend (in /preventivi/listino)
const API = {
  GET:         "/preventivi/listino/get.php",
  SAVE_BASE:   "/preventivi/listino/base_save.php",
  SAVE_PARAMS: "/preventivi/listino/params_save.php",
  UPLOAD:      "/preventivi/listino/upload.php",
};

// Canale per notificare altri tab che il listino è cambiato
const bc = "BroadcastChannel" in window ? new BroadcastChannel("listino") : null;
function broadcastListinoChanged() {
  try {
    const v = String(Date.now());
    localStorage.setItem("listinoVersion", v);
    if (bc) bc.postMessage({ type: "UPDATED", at: v });
  } catch {}
}

export default function ListinoAdmin() {
  // guard ruoli
  const { user, role: ctxRole } = useAuth();
  const roleRaw = String(ctxRole || user?.role_code || user?.ruolo || user?.role || "")
    .toLowerCase();
  const isAdmin = roleRaw === "admin" || roleRaw === "amministratore" || roleRaw === "root";

  // 🔧 gli state che mancavano
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [toast, setToast] = useState(null);

  // Righe del listino base
  const [baseRows, setBaseRows] = useState([]); // [{id,tipo,prezzo_base,coprifili_inclusi,img}]

  // Parametri globali
  const [params, setParams] = useState({
    coprifili_prezzo: "",
    coprifili_bonus_n: "",
    coprifili_bonus_costo: "",
    acc_zanzariera: "",
    acc_allargatori: "",
    scuri_g1: "",
    scuri_g2: "",
    scuri_g3: "",
    pers_g1: "",
    pers_g2: "",
    pers_g3: "",
    factor_zona_f: "",
    factor_tapparella: "",
    factor_cassonetto: "",
    factor_colore_premium: "",
    due_colori_once: "",
    costi_fissi_preventivo: "",
    costi_fissi_preventivo_fino3: "",
  });

  // Form per nuova riga base
  const [newRow, setNewRow] = useState({
    tipo: "",
    prezzo_base: "",
    coprifili_inclusi: "",
    file: null,
  });

  const headers = useMemo(
    () => ({ "Content-Type": "application/json", "Cache-Control": "no-store" }),
    []
  );
  const val = (v, d) => (v === undefined || v === null ? d : v);

  // piccolo toast
  const toastTimer = useRef(null);
  const showToast = (msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2200);
  };

  const loadListino = async () => {
    try {
      setLoading(true);
      setErr("");
      const r = await fetch(API.GET, { cache: "no-store" });
      if (!r.ok) throw new Error("Errore server (get_listino)");
      const data = await r.json();
      if (data.status !== "OK") throw new Error(data.message || "Errore");

      setBaseRows(data.base || []);
      setParams((p) => ({
        ...p,
        coprifili_prezzo: val(data.params?.coprifili_prezzo, 80),
        coprifili_bonus_n: val(data.params?.coprifili_bonus_n, 5),
        coprifili_bonus_costo: val(data.params?.coprifili_bonus_costo, 80),
        acc_zanzariera: val(data.params?.acc_zanzariera, 350),
        acc_allargatori: val(data.params?.acc_allargatori, 100),
        scuri_g1: val(data.params?.scuri_g1, 600),
        scuri_g2: val(data.params?.scuri_g2, 1200),
        scuri_g3: val(data.params?.scuri_g3, 1800),
        pers_g1: val(data.params?.pers_g1, 800),
        pers_g2: val(data.params?.pers_g2, 1400),
        pers_g3: val(data.params?.pers_g3, 2000),
        factor_zona_f: val(data.params?.factor_zona_f, 1.25),
        factor_tapparella: val(data.params?.factor_tapparella, 1.2),
        factor_cassonetto: val(data.params?.factor_cassonetto, 1.3),
        factor_colore_premium: val(data.params?.factor_colore_premium, 1.2),
        due_colori_once: val(data.params?.due_colori_once, 400),
        costi_fissi_preventivo: val(data.params?.costi_fissi_preventivo, 2000),
        costi_fissi_preventivo_fino3: val(data.params?.costi_fissi_preventivo_fino3, 1500),
      }));
    } catch (e) {
      console.error(e);
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.title = "Listino – Admin";
    loadListino();
    return () => clearTimeout(toastTimer.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function uploadImage(file) {
    const fd = new FormData();
    fd.append("file", file);
    const r = await fetch(API.UPLOAD, { method: "POST", body: fd });
    if (!r.ok) throw new Error("Upload fallito");
    const j = await r.json();
    if (j.status !== "OK") throw new Error(j.message || "Upload fallito");
    return j.url;
  }

  async function createBaseRow() {
    try {
      setErr("");
      let imgUrl = null;
      if (newRow.file) imgUrl = await uploadImage(newRow.file);

      const payload = {
        action: "create",
        tipo: (newRow.tipo || "").trim(), // preserva casing
        prezzo_base: Number(newRow.prezzo_base || 0),
        coprifili_inclusi: Number(newRow.coprifili_inclusi || 0),
        img: imgUrl,
      };

      const r = await fetch(API.SAVE_BASE, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      if (!r.ok) throw new Error("Errore creazione tipo");
      const j = await r.json();
      if (j.status !== "OK") throw new Error(j.message || "Errore creazione");

      setBaseRows((rows) => [...rows, { id: j.id, ...payload, img: imgUrl }]);
      setNewRow({ tipo: "", prezzo_base: "", coprifili_inclusi: "", file: null });
      showToast("Tipo inserito");
      broadcastListinoChanged();
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore");
    }
  }

  async function updateBaseRow(row) {
    try {
      setErr("");
      let imgUrl = row.img || null;
      if (row._file) imgUrl = await uploadImage(row._file);

      const payload = {
        action: "update",
        id: row.id,
        tipo: (row.tipo || "").trim(),
        prezzo_base: Number(row.prezzo_base || 0),
        coprifili_inclusi: Number(row.coprifili_inclusi || 0),
        img: imgUrl,
      };
      const r = await fetch(API.SAVE_BASE, { method: "POST", headers, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error("Errore aggiornamento");
      const j = await r.json();
      if (j.status !== "OK") throw new Error(j.message || "Errore aggiornamento");

      setBaseRows((rows) => rows.map((r0) => (r0.id === row.id ? { ...row, img: imgUrl, _file: undefined } : r0)));
      showToast("Salvato");
      broadcastListinoChanged();
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore");
    }
  }

  async function deleteBaseRow(id) {
    if (!window.confirm("Eliminare questo tipo?")) return;
    try {
      const r = await fetch(API.SAVE_BASE, { method: "POST", headers, body: JSON.stringify({ action: "delete", id }) });
      if (!r.ok) throw new Error("Errore eliminazione");
      const j = await r.json();
      if (j.status !== "OK") throw new Error(j.message || "Errore eliminazione");
      setBaseRows((rows) => rows.filter((r0) => r0.id !== id));
      showToast("Eliminato");
      broadcastListinoChanged();
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore");
    }
  }

  async function saveParams(section) {
    try {
      const payload = { section, params };
      const r = await fetch(API.SAVE_PARAMS, { method: "POST", headers, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error("Errore salvataggio parametri");
      const j = await r.json();
      if (j.status !== "OK") throw new Error(j.message || "Errore salvataggio");
      showToast("Parametri salvati");
      await loadListino();
      broadcastListinoChanged();
    } catch (e) {
      console.error(e);
      alert(e.message || "Errore");
    }
  }

  async function saveMultipliers() {
    await saveParams("moltiplicatori");
  }

  if (!isAdmin) return <div className="listino-y page-pad">Solo admin.</div>;

  return (
    <div className="listino-y">
      <header className="ly-toolbar">
        <div className="ly-titles">
          <h1>Listino – Admin</h1>
          <p className="muted">Gestisci prezzi base, accessori e moltiplicatori</p>
        </div>
        <div className="ly-actions">
          {loading ? <span className="chip chip-warn">Caricamento…</span> : <span className="chip chip-ok">Pronto</span>}
          <button className="btn-yellow-outline" onClick={loadListino}>↻ Ricarica</button>
        </div>
        {toast && <div className="toast">{toast} ✅</div>}
      </header>

      {err && <div className="alert-warn">{err}</div>}

      {/* ========================= PREZZI BASE ========================== */}
      <section className="card-ly">
        <h2>Prezzi base</h2>

        {/* Nuovo tipo */}
        <div className="add-row">
          <label className="field">
            <span>Tipo (es. F4A)</span>
            <input
              placeholder="Tipo (es. F4A)"
              value={newRow.tipo}
              onChange={(e) => setNewRow((s) => ({ ...s, tipo: e.target.value }))}
            />
          </label>

          <label className="field">
            <span>Prezzo base (€)</span>
            <input
              placeholder="Prezzo base €"
              type="number"
              inputMode="numeric"
              value={newRow.prezzo_base}
              onChange={(e) => setNewRow((s) => ({ ...s, prezzo_base: e.target.value }))}
            />
          </label>

          <label className="field">
            <span>Coprifili inclusi</span>
            <input
              placeholder="Coprifili inclusi"
              type="number"
              inputMode="numeric"
              value={newRow.coprifili_inclusi}
              onChange={(e) => setNewRow((s) => ({ ...s, coprifili_inclusi: e.target.value }))}
            />
          </label>

          <div className="add-actions">
            <input
              className="file"
              type="file"
              accept="image/*"
              onChange={(e) => setNewRow((s) => ({ ...s, file: (e.target.files && e.target.files[0]) || null }))}
              title="Scegli immagine"
            />
            <button className="btn-yellow" onClick={createBaseRow} disabled={!newRow.tipo}>
              + Aggiungi tipo
            </button>
          </div>
        </div>

        {/* Tabella */}
        <div className="grid-head">
          <strong>Img</strong>
          <strong>Tipo</strong>
          <strong>Prezzo base (€)</strong>
          <strong>Coprifili inclusi</strong>
          <span />
        </div>

        <div className="rows">
          {baseRows.map((row) => (
            <div key={row.id} className="grid-row">
              <div className="imgcell">
                {row.img ? <img src={row.img} alt="" className="thumb" /> : <div className="thumb placeholder" />}
                <input
                  className="file"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = (e.target.files && e.target.files[0]) || null;
                    setBaseRows((rows) => rows.map((r0) => (r0.id === row.id ? { ...r0, _file: f } : r0)));
                  }}
                />
              </div>

              <input
                value={row.tipo || ""}
                onChange={(e) => setBaseRows((rows) => rows.map((r0) => (r0.id === row.id ? { ...r0, tipo: e.target.value } : r0)))}
              />

              <input
                type="number"
                inputMode="numeric"
                value={row.prezzo_base ?? ""}
                onChange={(e) =>
                  setBaseRows((rows) => rows.map((r0) => (r0.id === row.id ? { ...r0, prezzo_base: e.target.value } : r0)))
                }
              />

              <input
                type="number"
                inputMode="numeric"
                value={row.coprifili_inclusi ?? ""}
                onChange={(e) =>
                  setBaseRows((rows) => rows.map((r0) => (r0.id === row.id ? { ...r0, coprifili_inclusi: e.target.value } : r0)))
                }
              />

              <div className="row-actions">
                <button className="btn-yellow-ghost" onClick={() => updateBaseRow(row)}>Salva</button>
                <button className="btn-danger" onClick={() => deleteBaseRow(row.id)}>🗑</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ========================= COPRIFILI ========================== */}
      <section className="card-ly">
        <h2>Coprifili</h2>
        <div className="grid grid-3">
          <label className="field">
            <span>Prezzo unitario (€)</span>
            <input
              type="number"
              value={params.coprifili_prezzo}
              onChange={(e) => setParams((p) => ({ ...p, coprifili_prezzo: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Bonus ogni N</span>
            <input
              type="number"
              value={params.coprifili_bonus_n}
              onChange={(e) => setParams((p) => ({ ...p, coprifili_bonus_n: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Costo bonus (€)</span>
            <input
              type="number"
              value={params.coprifili_bonus_costo}
              onChange={(e) => setParams((p) => ({ ...p, coprifili_bonus_costo: e.target.value }))}
            />
          </label>
        </div>
        <div className="card-actions">
          <button className="btn-yellow" onClick={() => saveParams("coprifili")}>Salva coprifili</button>
        </div>
      </section>

      {/* ========================= ACCESSORI FISSI ========================== */}
      <section className="card-ly">
        <h2>Accessori fissi</h2>
        <div className="grid grid-2">
          <label className="field">
            <span>Zanzariera (€ cad.)</span>
            <input
              type="number"
              value={params.acc_zanzariera}
              onChange={(e) => setParams((p) => ({ ...p, acc_zanzariera: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>Allargatori (€ cad.)</span>
            <input
              type="number"
              value={params.acc_allargatori}
              onChange={(e) => setParams((p) => ({ ...p, acc_allargatori: e.target.value }))}
            />
          </label>
        </div>
        <div className="card-actions">
          <button className="btn-yellow" onClick={() => saveParams("accessori")}>Salva accessori</button>
        </div>
      </section>

      {/* ========================= SCURI & PERSIANE ========================== */}
      <section className="card-ly">
        <h2>Scuri & Persiane (prezzi per gruppo)</h2>
        <div className="grid grid-3">
          <label className="field">
            <span>Scuri – Gruppo 1 (F1A / PF1A)</span>
            <input type="number" value={params.scuri_g1} onChange={(e) => setParams((p) => ({ ...p, scuri_g1: e.target.value }))} />
          </label>
          <label className="field">
            <span>Scuri – Gruppo 2 (F2A / PF2A)</span>
            <input type="number" value={params.scuri_g2} onChange={(e) => setParams((p) => ({ ...p, scuri_g2: e.target.value }))} />
          </label>
          <label className="field">
            <span>Scuri – Gruppo 3 (F3A / PF3A)</span>
            <input type="number" value={params.scuri_g3} onChange={(e) => setParams((p) => ({ ...p, scuri_g3: e.target.value }))} />
          </label>

          <label className="field">
            <span>Persiane – Gruppo 1 (F1A / PF1A)</span>
            <input type="number" value={params.pers_g1} onChange={(e) => setParams((p) => ({ ...p, pers_g1: e.target.value }))} />
          </label>
          <label className="field">
            <span>Persiane – Gruppo 2 (F2A / PF2A)</span>
            <input type="number" value={params.pers_g2} onChange={(e) => setParams((p) => ({ ...p, pers_g2: e.target.value }))} />
          </label>
          <label className="field">
            <span>Persiane – Gruppo 3 (F3A / PF3A)</span>
            <input type="number" value={params.pers_g3} onChange={(e) => setParams((p) => ({ ...p, pers_g3: e.target.value }))} />
          </label>
        </div>

        <div className="card-actions">
          <button className="btn-yellow-outline" onClick={() => saveParams("scuri_persiane")}>
            Salva scuri &amp; persiane
          </button>
        </div>
      </section>

      {/* ========================= MOLTIPLICATORI & COSTI ========================== */}
      <section className="card-ly">
        <h2>Moltiplicatori & Costi</h2>
        <div className="grid grid-3">
          <label className="field">
            <span>Zona F (×)</span>
            <input type="number" step="0.01" value={params.factor_zona_f}
                   onChange={(e) => setParams((p) => ({ ...p, factor_zona_f: e.target.value }))} />
          </label>
          <label className="field">
            <span>Tapparella (×)</span>
            <input type="number" step="0.01" value={params.factor_tapparella}
                   onChange={(e) => setParams((p) => ({ ...p, factor_tapparella: e.target.value }))} />
          </label>
          <label className="field">
            <span>Cassonetto (×)</span>
            <input type="number" step="0.01" value={params.factor_cassonetto}
                   onChange={(e) => setParams((p) => ({ ...p, factor_cassonetto: e.target.value }))} />
          </label>
          <label className="field">
            <span>Colore premium (×)</span>
            <input type="number" step="0.01" value={params.factor_colore_premium}
                   onChange={(e) => setParams((p) => ({ ...p, factor_colore_premium: e.target.value }))} />
          </label>
          <label className="field">
            <span>Due colori (una tantum €)</span>
            <input type="number" value={params.due_colori_once}
                   onChange={(e) => setParams((p) => ({ ...p, due_colori_once: e.target.value }))} />
          </label>
          <label className="field">
            <span>Costi fissi preventivo (default €)</span>
            <input type="number" value={params.costi_fissi_preventivo}
                   onChange={(e) => setParams((p) => ({ ...p, costi_fissi_preventivo: e.target.value }))} />
          </label>
          <label className="field">
            <span>Costi fissi – fino a 3 finestre (€)</span>
            <input type="number" value={params.costi_fissi_preventivo_fino3}
                   onChange={(e) => setParams((p) => ({ ...p, costi_fissi_preventivo_fino3: e.target.value }))} />
          </label>
        </div>

        <div className="card-actions">
          <button className="btn-yellow" onClick={saveMultipliers}>Salva moltiplicatori &amp; costi</button>
        </div>
      </section>
    </div>
  );
}
